/**
 * AdminProfessionalsTab — list + filter + create pane for the
 * marketplace admin console.
 *
 * Wave 5-Late Marketplace ANCHOR (content). Executive admin discipline:
 *   • Filter bar sits in a Section with hairline chrome, dense inputs.
 *   • List sits in its own Section, header row on
 *     `--ds-color-admin-surface-muted`.
 *   • Row hairlines via inline `border-top` (grid rows don't collapse
 *     like table rows do, so the border-color reliably paints without
 *     the border-collapse dance the Beta anchor needed).
 *   • Verification pill mapped to executive Badge tones
 *     (`neutralMuted` / `warning` / `info` / `successMuted` / `danger`) —
 *     replaces raw rgba tokens returned by `statusBadge()` on this
 *     surface only (helper preserved for other consumers).
 *   • Category chips restyled to admin-tone (neutralMuted-adjacent
 *     background + ink-muted text), losing the purple pastel look.
 *   • Row density `py-2` (grid rows read denser than table rows at the
 *     same v-padding — calibrated per the reviewer's Beta note).
 *
 * Business logic (fetch, filter, search, category prop) and testids
 * preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, ExternalLink, Plus, RefreshCw, Search } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { labelForSlug } from "./verificationFsm";
import { Badge } from "@/design-system/components/Badge";
import ProfessionalCreateModal from "./ProfessionalCreateModal";
import { AccentButton, GhostButton, Section, SelectInput } from "@/admin/_primitives";

const STATUS_FILTERS = ["all", "pending", "approved", "active", "rejected"];

// Anchor-local mapping from verification_status → executive Badge tone.
// Bypasses the raw-rgba `statusBadge()` helper on this surface without
// touching the helper (still consumed by ProfessionalDetail + tests +
// the consumer readiness card).
//
// v2 fix (Marketplace anchor gate v1 → v2): `approved` demoted from
// `info` (saturated blue) to `neutralMuted` (slate). Reviewer flagged
// both APPROVED and ACTIVE as too saturated — desaturated soft palette
// applied to APPROVED, and success-muted-soft steel-tinted for ACTIVE.
const VERIFICATION_TONE = {
  pending: "warning",
  approved: "neutralMuted",
  active: "successMuted",
  rejected: "danger",
};

/* ------------------------------------------------------------------ */
/* Local admin primitives (mirror BetaCodes v2 / Wave 5-Early anchors). */
/* ------------------------------------------------------------------ */


function Field({ label, children }) {
  return (
    <label className="block">
      <span
        className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}




/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

const GRID_COLS =
  "grid-cols-[minmax(220px,2fr)_1fr_140px_90px_70px_70px_50px]";

export default function AdminProfessionalsTab({ categories }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("all");
  const [includeDemo, setIncludeDemo] = useState(true);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 200, include_demo: includeDemo };
      if (status !== "all") params.verification_status = status;
      const { data } = await api.get("/admin/marketplace/professionals", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(formatApiError(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, includeDemo]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(needle) ||
        (r.contact_email || "").toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const filterBadge = (
    <div className="flex items-center gap-2">
      <GhostButton
        onClick={fetchRows}
        data-testid="admin-pro-refresh"
        className="!py-1 !text-[11px]"
      >
        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
      </GhostButton>
      <AccentButton
        onClick={() => setCreateOpen(true)}
        data-testid="admin-pro-create-btn"
      >
        <Plus className="w-3.5 h-3.5" />
        New professional
      </AccentButton>
    </div>
  );

  return (
    <div>
      <Section
        title="Filter professionals"
        icon={Search}
        badge={filterBadge}
        testid="admin-pro-filters"
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Field label="Search">
              <div className="relative">
                <Search
                  className="absolute top-1/2 -translate-y-1/2 left-2.5 w-3.5 h-3.5 pointer-events-none"
                  style={{ color: "var(--ds-color-ink-muted)" }}
                  aria-hidden="true"
                />
                <input
                  data-testid="admin-pro-search"
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or email"
                  className="w-full rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] pl-8 pr-2.5 py-1.5 text-[13px] text-[var(--ds-color-ink)] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
                  style={{ borderColor: "var(--ds-color-input-border)" }}
                />
              </div>
            </Field>
          </div>
          <Field label="Verification">
            <SelectInput
              data-testid="admin-pro-status-filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All statuses" : s}
                </option>
              ))}
            </SelectInput>
          </Field>
          <label
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] pb-1.5"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            <input
              type="checkbox"
              data-testid="admin-pro-include-demo"
              checked={includeDemo}
              onChange={(e) => setIncludeDemo(e.target.checked)}
              className="accent-[var(--ds-color-accent)]"
            />
            Include demo profiles
          </label>
        </div>
      </Section>

      {error && (
        <div
          data-testid="admin-pro-error"
          className="mb-4 rounded-[var(--ds-radius-field)] border px-3 py-2 text-[12px]"
          style={{
            borderColor: "var(--ds-color-danger)",
            color: "var(--ds-color-danger)",
            background: "var(--ds-color-danger-soft)",
          }}
        >
          {error}
        </div>
      )}

      <Section
        title="Professionals"
        icon={Briefcase}
        badge={
          <span
            className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            {filtered.length} showing · {rows.length} total
          </span>
        }
        testid="admin-pro-list-section"
        bodyClassName="p-0"
      >
        <div className="overflow-x-auto" data-testid="admin-pro-list">
          <div
            className={`grid ${GRID_COLS} px-4 py-2 text-[10px] uppercase tracking-[0.16em] font-semibold gap-3`}
            style={{
              color: "var(--ds-color-ink-subtle)",
              background: "var(--ds-color-admin-surface-muted)",
              borderBottom: "1px solid var(--ds-color-admin-border)",
            }}
          >
            <span>Name</span>
            <span>Categories</span>
            <span>Verification</span>
            <span>Active</span>
            <span>Demo</span>
            <span>Yrs</span>
            <span />
          </div>

          {loading ? (
            <div
              className="px-4 py-8 text-[13px] text-center"
              style={{ color: "var(--ds-color-ink-muted)" }}
              data-testid="admin-pro-loading"
            >
              Loading professionals…
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="px-4 py-10 text-[13px] text-center"
              style={{ color: "var(--ds-color-ink-muted)" }}
              data-testid="admin-pro-empty"
            >
              No professionals match those filters.
            </div>
          ) : (
            filtered.map((p, idx) => {
              const tone = VERIFICATION_TONE[p.verification_status] || "neutralMuted";
              const isLast = idx === filtered.length - 1;
              return (
                <Link
                  key={p.id}
                  to={`/admin/marketplace/professionals/${p.id}`}
                  data-testid="admin-pro-list-row"
                  data-pro-id={p.id}
                  data-verification-status={p.verification_status}
                  data-is-active={p.is_active ? "true" : "false"}
                  className={`grid ${GRID_COLS} px-4 py-1.5 gap-3 text-[13px] items-center transition-colors`}
                  style={{
                    borderBottom: isLast
                      ? "none"
                      : "1px solid var(--ds-color-admin-border)",
                    opacity: p.is_active ? 1 : 0.62,
                    transitionDuration: "var(--ds-duration-fast)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background =
                      "var(--ds-color-admin-surface-muted)")
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div className="min-w-0">
                    <div
                      className="font-medium truncate"
                      style={{ color: "var(--ds-color-ink)" }}
                    >
                      {p.name}
                    </div>
                    <div
                      className="text-[11px] truncate"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {p.contact_email}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 min-w-0">
                    {(p.categories || []).slice(0, 3).map((s) => (
                      <span
                        key={s}
                        className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-[var(--ds-radius-input)]"
                        style={{
                          background: "var(--ds-color-admin-surface-inset)",
                          color: "var(--ds-color-ink-muted)",
                          border: "1px solid var(--ds-color-admin-border)",
                        }}
                      >
                        {labelForSlug(s)}
                      </span>
                    ))}
                    {(p.categories || []).length > 3 && (
                      <span
                        className="text-[10px]"
                        style={{ color: "var(--ds-color-ink-subtle)" }}
                      >
                        +{p.categories.length - 3}
                      </span>
                    )}
                  </div>
                  <Badge
                    tone={tone}
                    size="sm"
                    shape="sharp"
                    className="uppercase tracking-[0.14em] w-fit"
                  >
                    {p.verification_status}
                  </Badge>
                  <span
                    className="text-[12px] tabular-nums"
                    style={{
                      color: p.is_active
                        ? "var(--ds-color-ink)"
                        : "var(--ds-color-ink-muted)",
                    }}
                  >
                    {p.is_active ? "Yes" : "No"}
                  </span>
                  <span
                    className="text-[12px]"
                    style={{
                      color: p.is_demo
                        ? "var(--ds-color-accent-strong)"
                        : "var(--ds-color-ink-muted)",
                    }}
                  >
                    {p.is_demo ? "Demo" : "—"}
                  </span>
                  <span
                    className="text-[12px] tabular-nums"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                  >
                    {p.years_experience}
                  </span>
                  <ExternalLink
                    className="w-3.5 h-3.5 justify-self-end"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                    aria-hidden="true"
                  />
                </Link>
              );
            })
          )}
        </div>
      </Section>

      <ProfessionalCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        categories={categories}
        onCreated={(newPro) => {
          setRows((prev) => [newPro, ...prev]);
        }}
      />
    </div>
  );
}
